import { Injectable } from '@angular/core';
import { StateSnapshot } from '../interfaces/state-snapshot.interface';
import { StorageStrategy } from '../models/route-config.model';

/**
 * Multi-tier storage service for state persistence
 *
 * Implements a 3-tier storage hierarchy:
 * 1. Memory (Map) - Fastest, lost on refresh
 * 2. SessionStorage - Survives refresh, lost on tab close
 * 3. LocalStorage - Persists across sessions
 *
 * Strategy Selection:
 * - MEMORY_ONLY: Memory cache only
 * - SESSION: Memory + SessionStorage
 * - LOCAL: Memory + SessionStorage + LocalStorage
 * - AUTO: Selects based on cache duration
 */
@Injectable({
  providedIn: 'root'
})
export class StateStorageService {
  private readonly STORAGE_PREFIX = 'crm_state_';
  private readonly memoryCache = new Map<string, StateSnapshot>();

  constructor() {
    this.initializeCleanup();
  }

  /**
   * Store state snapshot using specified strategy
   */
  store(key: string, snapshot: StateSnapshot, strategy: StorageStrategy = StorageStrategy.AUTO): void {
    const resolvedStrategy = this.resolveStrategy(strategy, snapshot.expiresAt - snapshot.timestamp);

    // Always store in memory for fast access
    this.memoryCache.set(key, snapshot);

    // Store in SessionStorage if strategy requires
    if (resolvedStrategy === StorageStrategy.SESSION || resolvedStrategy === StorageStrategy.LOCAL) {
      this.storeInSessionStorage(key, snapshot);
    }

    // Store in LocalStorage if strategy requires
    if (resolvedStrategy === StorageStrategy.LOCAL) {
      this.storeInLocalStorage(key, snapshot);
    }

    // console.log(`[StateStorage] Stored state for key: ${key} using strategy: ${resolvedStrategy}`);
  }

  /**
   * Retrieve state snapshot (checks all storage tiers)
   */
  retrieve(key: string): StateSnapshot | null {
    // Try memory first (fastest)
    let snapshot = this.memoryCache.get(key);
    if (snapshot && this.isValid(snapshot)) {
      return snapshot;
    }

    // Try SessionStorage
    snapshot = this.retrieveFromSessionStorage(key);
    if (snapshot && this.isValid(snapshot)) {
      // Restore to memory cache
      this.memoryCache.set(key, snapshot);
      return snapshot;
    }

    // Try LocalStorage
    snapshot = this.retrieveFromLocalStorage(key);
    if (snapshot && this.isValid(snapshot)) {
      // Restore to memory cache
      this.memoryCache.set(key, snapshot);
      return snapshot;
    }

    // No valid state found
    return null;
  }

  /**
   * Check if state exists for a key
   */
  has(key: string): boolean {
    return this.retrieve(key) !== null;
  }

  /**
   * Remove state for a specific key
   */
  remove(key: string): void {
    this.memoryCache.delete(key);
    this.removeFromSessionStorage(key);
    this.removeFromLocalStorage(key);
    // console.log(`[StateStorage] Removed state for key: ${key}`);
  }

  /**
   * Clear all stored states
   */
  clearAll(): void {
    this.memoryCache.clear();
    this.clearSessionStorage();
    this.clearLocalStorage();
    console.log('[StateStorage] Cleared all stored states');
  }

  /**
   * Clear expired states from all storage tiers
   */
  clearExpired(): void {
    const now = Date.now();
    let expiredCount = 0;

    // Clear from memory
    for (const [key, snapshot] of this.memoryCache.entries()) {
      if (snapshot.expiresAt < now) {
        this.memoryCache.delete(key);
        expiredCount++;
      }
    }

    // Clear from SessionStorage
    expiredCount += this.clearExpiredFromStorage(sessionStorage);

    // Clear from LocalStorage
    expiredCount += this.clearExpiredFromStorage(localStorage);

    if (expiredCount > 0) {
      console.log(`[StateStorage] Cleared ${expiredCount} expired states`);
    }
  }

  /**
   * Get storage statistics
   */
  getStats(): {
    memoryCount: number;
    sessionCount: number;
    localCount: number;
    totalSize: string;
  } {
    const sessionCount = this.countItemsInStorage(sessionStorage);
    const localCount = this.countItemsInStorage(localStorage);

    return {
      memoryCount: this.memoryCache.size,
      sessionCount,
      localCount,
      totalSize: this.estimateTotalSize()
    };
  }

  /**
   * Get all stored keys
   */
  getAllKeys(): string[] {
    const keys = new Set<string>();

    // From memory
    this.memoryCache.forEach((_, key) => keys.add(key));

    // From SessionStorage
    for (let i = 0; i < sessionStorage.length; i++) {
      const key = sessionStorage.key(i);
      if (key?.startsWith(this.STORAGE_PREFIX)) {
        keys.add(key.replace(this.STORAGE_PREFIX, ''));
      }
    }

    // From LocalStorage
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key?.startsWith(this.STORAGE_PREFIX)) {
        keys.add(key.replace(this.STORAGE_PREFIX, ''));
      }
    }

    return Array.from(keys);
  }

  // ============================================================================
  // PRIVATE METHODS
  // ============================================================================

  /**
   * Resolve storage strategy (AUTO → specific strategy)
   */
  private resolveStrategy(strategy: StorageStrategy, duration: number): StorageStrategy {
    if (strategy !== StorageStrategy.AUTO) {
      return strategy;
    }

    // Auto-select based on duration
    if (duration < 300000) { // < 5 minutes
      return StorageStrategy.MEMORY_ONLY;
    } else if (duration < 1800000) { // < 30 minutes
      return StorageStrategy.SESSION;
    } else {
      return StorageStrategy.LOCAL;
    }
  }

  /**
   * Check if snapshot is still valid
   */
  private isValid(snapshot: StateSnapshot): boolean {
    return snapshot.expiresAt > Date.now();
  }

  /**
   * Store in SessionStorage
   */
  private storeInSessionStorage(key: string, snapshot: StateSnapshot): void {
    try {
      const storageKey = this.STORAGE_PREFIX + key;
      sessionStorage.setItem(storageKey, JSON.stringify(snapshot));
    } catch (error) {
      console.warn('[StateStorage] Failed to store in SessionStorage:', error);
      // Handle quota exceeded error
      if (error instanceof DOMException && error.name === 'QuotaExceededError') {
        this.clearOldestFromStorage(sessionStorage);
        // Retry once
        try {
          sessionStorage.setItem(this.STORAGE_PREFIX + key, JSON.stringify(snapshot));
        } catch (retryError) {
          console.error('[StateStorage] Failed to store even after cleanup:', retryError);
        }
      }
    }
  }

  /**
   * Retrieve from SessionStorage
   */
  private retrieveFromSessionStorage(key: string): StateSnapshot | null {
    try {
      const storageKey = this.STORAGE_PREFIX + key;
      const data = sessionStorage.getItem(storageKey);
      if (!data) return null;

      return JSON.parse(data) as StateSnapshot;
    } catch (error) {
      console.warn('[StateStorage] Failed to retrieve from SessionStorage:', error);
      return null;
    }
  }

  /**
   * Remove from SessionStorage
   */
  private removeFromSessionStorage(key: string): void {
    try {
      sessionStorage.removeItem(this.STORAGE_PREFIX + key);
    } catch (error) {
      console.warn('[StateStorage] Failed to remove from SessionStorage:', error);
    }
  }

  /**
   * Store in LocalStorage
   */
  private storeInLocalStorage(key: string, snapshot: StateSnapshot): void {
    try {
      const storageKey = this.STORAGE_PREFIX + key;
      localStorage.setItem(storageKey, JSON.stringify(snapshot));
    } catch (error) {
      console.warn('[StateStorage] Failed to store in LocalStorage:', error);
      if (error instanceof DOMException && error.name === 'QuotaExceededError') {
        this.clearOldestFromStorage(localStorage);
        try {
          localStorage.setItem(this.STORAGE_PREFIX + key, JSON.stringify(snapshot));
        } catch (retryError) {
          console.error('[StateStorage] Failed to store even after cleanup:', retryError);
        }
      }
    }
  }

  /**
   * Retrieve from LocalStorage
   */
  private retrieveFromLocalStorage(key: string): StateSnapshot | null {
    try {
      const storageKey = this.STORAGE_PREFIX + key;
      const data = localStorage.getItem(storageKey);
      if (!data) return null;

      return JSON.parse(data) as StateSnapshot;
    } catch (error) {
      console.warn('[StateStorage] Failed to retrieve from LocalStorage:', error);
      return null;
    }
  }

  /**
   * Remove from LocalStorage
   */
  private removeFromLocalStorage(key: string): void {
    try {
      localStorage.removeItem(this.STORAGE_PREFIX + key);
    } catch (error) {
      console.warn('[StateStorage] Failed to remove from LocalStorage:', error);
    }
  }

  /**
   * Clear all items from SessionStorage
   */
  private clearSessionStorage(): void {
    const keysToRemove: string[] = [];
    for (let i = 0; i < sessionStorage.length; i++) {
      const key = sessionStorage.key(i);
      if (key?.startsWith(this.STORAGE_PREFIX)) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach(key => sessionStorage.removeItem(key));
  }

  /**
   * Clear all items from LocalStorage
   */
  private clearLocalStorage(): void {
    const keysToRemove: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key?.startsWith(this.STORAGE_PREFIX)) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach(key => localStorage.removeItem(key));
  }

  /**
   * Clear expired items from specific storage
   */
  private clearExpiredFromStorage(storage: Storage): number {
    const now = Date.now();
    let count = 0;
    const keysToRemove: string[] = [];

    for (let i = 0; i < storage.length; i++) {
      const key = storage.key(i);
      if (key?.startsWith(this.STORAGE_PREFIX)) {
        try {
          const data = storage.getItem(key);
          if (data) {
            const snapshot = JSON.parse(data) as StateSnapshot;
            if (snapshot.expiresAt < now) {
              keysToRemove.push(key);
              count++;
            }
          }
        } catch (error) {
          // Invalid data, remove it
          keysToRemove.push(key);
          count++;
        }
      }
    }

    keysToRemove.forEach(key => storage.removeItem(key));
    return count;
  }

  /**
   * Clear oldest item from storage (for quota management)
   */
  private clearOldestFromStorage(storage: Storage): void {
    let oldestKey: string | null = null;
    let oldestTimestamp = Date.now();

    for (let i = 0; i < storage.length; i++) {
      const key = storage.key(i);
      if (key?.startsWith(this.STORAGE_PREFIX)) {
        try {
          const data = storage.getItem(key);
          if (data) {
            const snapshot = JSON.parse(data) as StateSnapshot;
            if (snapshot.timestamp < oldestTimestamp) {
              oldestTimestamp = snapshot.timestamp;
              oldestKey = key;
            }
          }
        } catch (error) {
          // Invalid data, could be a candidate for removal
        }
      }
    }

    if (oldestKey) {
      storage.removeItem(oldestKey);
      console.log(`[StateStorage] Removed oldest item: ${oldestKey}`);
    }
  }

  /**
   * Count items in storage
   */
  private countItemsInStorage(storage: Storage): number {
    let count = 0;
    for (let i = 0; i < storage.length; i++) {
      const key = storage.key(i);
      if (key?.startsWith(this.STORAGE_PREFIX)) {
        count++;
      }
    }
    return count;
  }

  /**
   * Estimate total size of stored data
   */
  private estimateTotalSize(): string {
    let totalBytes = 0;

    // Memory size
    this.memoryCache.forEach(snapshot => {
      totalBytes += JSON.stringify(snapshot).length * 2; // UTF-16 characters
    });

    // SessionStorage size
    for (let i = 0; i < sessionStorage.length; i++) {
      const key = sessionStorage.key(i);
      if (key?.startsWith(this.STORAGE_PREFIX)) {
        const data = sessionStorage.getItem(key);
        if (data) {
          totalBytes += (key.length + data.length) * 2;
        }
      }
    }

    // LocalStorage size
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key?.startsWith(this.STORAGE_PREFIX)) {
        const data = localStorage.getItem(key);
        if (data) {
          totalBytes += (key.length + data.length) * 2;
        }
      }
    }

    return this.formatBytes(totalBytes);
  }

  /**
   * Format bytes to human-readable string
   */
  private formatBytes(bytes: number): string {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(2) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(2) + ' MB';
  }

  /**
   * Initialize automatic cleanup
   */
  private initializeCleanup(): void {
    // Clear expired states every 5 minutes
    setInterval(() => {
      this.clearExpired();
    }, 300000); // 5 minutes

    // Clear on page unload (for memory cache)
    if (typeof window !== 'undefined') {
      window.addEventListener('beforeunload', () => {
        // Memory cache will be cleared automatically
        // SessionStorage and LocalStorage persist
      });
    }
  }
}
